"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { COUNTRY_OPTIONS, type CountryOption } from "@/lib/explore-utils";
import { useOutsideClick } from "@/lib/use-outside-click";
import { cn } from "@/lib/utils";

type BaseProps = {
  label?: string;
  helperText?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
};

type SingleProps = BaseProps & {
  multiple?: false;
  value: string | null;
  onChange: (value: string | null) => void;
};

type MultiProps = BaseProps & {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
};

type Props = SingleProps | MultiProps;

function Flag({ option }: { option: CountryOption }) {
  return (
    <img
      src={option.flagUrl}
      alt=""
      aria-hidden="true"
      className="size-4 rounded-sm object-cover shrink-0"
      loading="lazy"
    />
  );
}

export function CountryPicker(props: Props) {
  const { label, helperText, placeholder, className, disabled } = props;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useOutsideClick(rootRef, () => setOpen(false), { enabled: open, escape: true });

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 0);
    else setQuery("");
  }, [open]);

  const selected = useMemo(() => {
    if (props.multiple) {
      const set = new Set(props.value);
      return COUNTRY_OPTIONS.filter((c) => set.has(c.name));
    }
    return props.value
      ? COUNTRY_OPTIONS.find((c) => c.name === props.value) ?? null
      : null;
  }, [props]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRY_OPTIONS;
    return COUNTRY_OPTIONS.filter(
      (c) =>
        c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [query]);

  const isSelected = (name: string) => {
    if (props.multiple) return props.value.includes(name);
    return props.value === name;
  };

  const toggle = (name: string) => {
    if (props.multiple) {
      const next = props.value.includes(name)
        ? props.value.filter((v) => v !== name)
        : [...props.value, name];
      props.onChange(next);
    } else {
      props.onChange(props.value === name ? null : name);
      setOpen(false);
    }
  };

  const remove = (name: string) => {
    if (props.multiple) props.onChange(props.value.filter((v) => v !== name));
    else props.onChange(null);
  };

  const triggerLabel =
    !props.multiple && selected && !Array.isArray(selected) ? (
      <span className="inline-flex items-center gap-[var(--space-2)] truncate">
        <Flag option={selected} />
        <span className="truncate">{selected.name}</span>
      </span>
    ) : (
      <span className="text-[var(--color-text-muted)]">
        {placeholder ??
          (props.multiple ? "Select countries…" : "Select a country…")}
      </span>
    );

  return (
    <div className={cn("flex flex-col gap-[var(--space-1-5)]", className)}>
      {label && (
        <span className="text-[length:var(--font-size-sm)] font-[number:var(--font-weight-medium)] text-[var(--color-text-primary)]">
          {label}
        </span>
      )}
      <div ref={rootRef} className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            "flex h-9 w-full items-center justify-between gap-[var(--space-2)]",
            "rounded-[var(--radius-md)] border bg-transparent",
            "px-[var(--space-3)] py-[var(--space-2)]",
            "text-[length:var(--font-size-sm)] text-[var(--color-text-primary)]",
            "transition-colors duration-[var(--duration-normal)] outline-none",
            "border-[var(--color-border-default)]",
            "focus-visible:border-[var(--color-border-focus)]",
            "focus-visible:ring-[3px] focus-visible:ring-[var(--color-primary-muted)]",
            "disabled:pointer-events-none disabled:cursor-not-allowed",
            "disabled:bg-[var(--color-bg-muted)] disabled:opacity-50",
          )}
        >
          <span className="flex-1 truncate text-left">{triggerLabel}</span>
          <ChevronDown
            className={cn(
              "size-4 text-[var(--color-text-muted)] transition-transform",
              open && "rotate-180",
            )}
          />
        </button>

        {open && (
          <div
            className={cn(
              "absolute z-50 mt-[var(--space-1)] w-full",
              "rounded-[var(--radius-md)] border border-[var(--color-border-default)]",
              "bg-[var(--color-bg-card)] shadow-lg",
              "overflow-hidden",
            )}
          >
            <div className="flex items-center gap-[var(--space-2)] border-b border-[var(--color-border-default)] px-[var(--space-3)] py-[var(--space-2)]">
              <Search className="size-4 text-[var(--color-text-muted)]" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search countries…"
                className="w-full bg-transparent text-[length:var(--font-size-sm)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] outline-none"
              />
            </div>
            <ul
              role="listbox"
              aria-multiselectable={props.multiple || undefined}
              className="max-h-64 overflow-y-auto py-[var(--space-1)]"
            >
              {filtered.length === 0 ? (
                <li className="px-[var(--space-3)] py-[var(--space-2)] text-[length:var(--font-size-sm)] text-[var(--color-text-muted)]">
                  No countries match.
                </li>
              ) : (
                filtered.map((c) => {
                  const active = isSelected(c.name);
                  return (
                    <li key={c.code}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => toggle(c.name)}
                        className={cn(
                          "flex w-full items-center gap-[var(--space-2)]",
                          "px-[var(--space-3)] py-[var(--space-2)]",
                          "text-left text-[length:var(--font-size-sm)]",
                          "text-[var(--color-text-primary)]",
                          "hover:bg-[var(--color-bg-muted)]",
                          active && "bg-[var(--color-accent)]/10",
                        )}
                      >
                        <Flag option={c} />
                        <span className="flex-1 truncate">{c.name}</span>
                        {active && (
                          <span className="text-[length:var(--font-size-xs)] text-[var(--color-accent)]">
                            ✓
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        )}
      </div>

      {props.multiple && Array.isArray(selected) && selected.length > 0 && (
        <div className="flex flex-wrap gap-[var(--space-1)]">
          {selected.map((c) => (
            <Badge key={c.code} variant="secondary">
              <span className="inline-flex items-center gap-[var(--space-1)]">
                <Flag option={c} />
                {c.name}
                <button
                  type="button"
                  onClick={() => remove(c.name)}
                  aria-label={`Remove ${c.name}`}
                  className="ml-[var(--space-1)] hover:text-[var(--color-error)]"
                >
                  <X className="size-3" />
                </button>
              </span>
            </Badge>
          ))}
        </div>
      )}

      {helperText && (
        <p className="text-[length:var(--font-size-xs)] text-[var(--color-text-muted)]">
          {helperText}
        </p>
      )}
    </div>
  );
}
